/**
 * La barra lateral: el estudio arriba, los agentes editoriales debajo.
 *
 * REDISEÑO DEL 11-sep-2026. Dos bloques: las seis secciones del estudio (Modelos · Autores ·
 * Estilos · Proyectos · Manuscrito · Auditoría) y los cuatro agentes con el chat de reescritura.
 * Los dieciocho colaboradores de antes no desaparecen: se agrupan dentro de los cuatro, y los de
 * publicación salen por la acción «Publicar». La lista de gente sigue enseñando lo último que te
 * dijeron, porque eso sigue siendo verdad.
 *
 * POR QUÉ ESTO Y NO UN MENÚ. Lo que HumanInk vende no es un panel con funciones: es un equipo
 * editorial. Un menú de secciones dice «esta aplicación hace ocho cosas»; una lista de personas
 * con lo último que te dijeron dice «tienes ocho conversaciones abiertas sobre tu libro», que es
 * lo que de verdad hay. El trabajo —informes, pasadas, bitácora— se fue a la barra de arriba,
 * donde se ve entero de un vistazo.
 *
 * La previsualización sale del hilo guardado en la carpeta del manuscrito, no de la memoria del
 * navegador: si al recargar desaparecieran las conversaciones, la barra estaría enseñando que la
 * aplicación olvida — justo lo contrario de lo que promete un producto que registra todo lo que
 * se hace.
 *
 * Aquí no se inventa ningún contador. A quien no se le ha encargado nada todavía se le enseña su
 * oficio, en gris y sin hora, y no una conversación que no existe.
 */
import { useEffect, useRef, useState } from "react";
import { useClerk, useUser } from "@clerk/react";
import Cara from "./Cara";
import { AGENTES, COACH, HIJAS, SECCIONES, SECCIONES_FLUJO } from "./Barra";
import type { Item } from "./Barra";
import { useFlujo } from "../flujo";
import { cuando, hilo, hiloReescritura, salir } from "../api";
import { coincidencias } from "../logica";
import type { Colaborador, Asomo } from "../api";

export default function Lateral(
  { equipo, bandeja, hablando, alHablar, gasto, version, alIr, alSalir, cerrado, nombre, seccion, actual, cuentas = {}, dir = "" }: {
    equipo: Colaborador[];
    bandeja: Asomo[];
    hablando: string | null;
    alHablar: (id: string) => void;
    gasto: number;
    version: string;
    alIr: (id: string) => void;
    alSalir: () => void; cerrado?: boolean; nombre?: string | null;
    seccion: string;                                    // la sección encendida (ya resuelta a su padre)
    actual?: string;                                    // la pantalla de verdad, para encender la hija
    cuentas?: Record<string, string | number | undefined>;
    dir?: string;                                       // la carpeta del proyecto: sus hilos se buscan
  },
) {
  const [menu, setMenu] = useState(false);
  const [busca, setBusca] = useState("");
  // EL STUDIO COMO FLUJO (3-oct-2026): seis secciones, sin hijas, y un solo interlocutor.
  const flujo = useFlujo();
  const secciones = flujo ? SECCIONES_FLUJO : SECCIONES;
  const hijas: Record<string, Item[]> = flujo ? {} : HIJAS;
  const gente = flujo ? [COACH] : AGENTES;
  // Quién eres, de la CUENTA. Antes el avatar decía «RB» a fuego para cualquier cliente, y el
  // nombre salía del email de la licencia de Word. La licencia queda como plan B.
  const { user } = useUser();
  const clerk = useClerk();
  const quien = user?.firstName || user?.primaryEmailAddress?.emailAddress?.split("@")[0] || nombre || "Tu cuenta";
  const iniciales = (user?.fullName || quien).split(/\s+/).filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  const alternarTema = () => {
    const r = document.documentElement;
    const oscuro = r.getAttribute("data-theme") === "dark" ||
      (!r.getAttribute("data-theme") && matchMedia("(prefers-color-scheme:dark)").matches);
    r.setAttribute("data-theme", oscuro ? "light" : "dark");
  };

  const ir = (id: string) => { alIr(id); setMenu(false); };

  const porId = new Map(bandeja.map((a) => [a.id, a]));
  const q = busca.trim().toLowerCase();
  const cabe = (c: Colaborador) =>
    !q || c.name.toLowerCase().includes(q) || c.phase.toLowerCase().includes(q) ||
    (porId.get(c.id)?.ultimo.texto ?? "").toLowerCase().includes(q);

  // BUSCAR EN EL CHAT (17-sep, Rais: «ya que tenemos cuatro agentes, mejor buscar en el chat»).
  // Los hilos se piden al motor una vez por búsqueda —cuando se empieza a escribir— y se buscan
  // aquí; no se vuelven a pedir en cada tecla.
  const [hilos, setHilos] = useState<Record<string, { texto: string; mio: boolean }[]> | null>(null);
  const pedidos = useRef("");
  useEffect(() => {
    if (q.length < 3 || !dir) { if (!q) { setHilos(null); pedidos.current = ""; } return; }
    if (pedidos.current === dir) return;
    pedidos.current = dir;
    Promise.all([
      ...gente.map((a) => hilo(a.id, dir)
        .then((h) => [a.id, h.map((m) => ({ texto: m.content, mio: m.role === "user" }))] as const)
        .catch(() => [a.id, []] as const)),
      // El chat de reescritura también: ahí habla el editor con el fantasma y el coach. En el
      // Studio nuevo no existe: solo se busca en la conversación con el coach.
      ...(flujo ? [] : [hiloReescritura(dir)
        .then((h) => ["reescritura", h.map((m) => ({ texto: m.content, mio: m.role === "user" }))] as const)
        .catch(() => ["reescritura", []] as const)]),
    ])
      .then((pares) => setHilos(Object.fromEntries(pares)));
  }, [q, dir]);
  const hallazgos = q.length >= 3 && hilos
    ? [...gente, ...(flujo ? [] : [{ id: "reescritura", nombre: "Chat de reescritura", oficio: "" }])]
        .flatMap((a) => coincidencias(hilos[a.id] ?? [], q).map((h) => ({ ...h, agente: a })))
    : [];

  // Solo los cuatro agentes, en el orden del documento y con SU nombre de agente: el motor sigue
  // llamando «Corrector» a copyeditor, pero el rótulo lo pone esta lista, no el motor.
  const agentes = gente
    .map((a) => { const c = equipo.find((x) => x.id === a.id); return c ? { ...c, name: a.nombre, goal: a.oficio } : null; })
    .filter((c): c is Colaborador => !!c)
    .filter(cabe);

  const fila = (c: Colaborador) => {
    const a = porId.get(c.id);
    return (
      <button
        key={c.id}
        className={"conv" + (hablando === c.id ? " on" : "")}
        onClick={() => alHablar(c.id)}
        title={c.goal}
      >
        <Cara id={c.id} nombre={c.name} />
        <span className="qui">
          <span className="l1">
            <b>{c.name}</b>
            <span className="ofi">{c.phase}</span>
            <span className="hora">{a ? cuando(a.ultimo.at) : ""}</span>
          </span>
          <span className={"l2" + (a ? "" : " oficio")}>
            {a ? (a.ultimo.mio ? "Tú: " : "") + a.ultimo.texto : c.goal}
          </span>
        </span>
      </button>
    );
  };

  return (
    <aside className="lat">
      <div className="marca">
        {/* El logo de la marca, con su aro cian↔oro: cian lo humano, oro la máquina. Es la misma
            imagen de la web y de la tienda; el producto no se presenta de dos maneras. */}
        <img src="/humanink.png" alt="" width="26" height="26" />
        <b>HumanInk</b><span>Editor Studio</span>
      </div>

      <div className="buscar">
        <span>⌕</span>
        <input
          className="escribir" placeholder={flujo ? "Buscar en lo hablado con el coach" : "Buscar en tus conversaciones"}
          value={busca} onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      <div className="convs">
        {!q && (
          <>
            <div className="grupo">Estudio</div>
            <div className="items">
              {secciones.map((s) => (
                <div key={s.id} style={{ display: "contents" }}>
                  <button className={"it" + ((flujo || !hablando) && seccion === s.id && (flujo || !actual || actual === s.id || actual === "panel") ? " on" : "")} onClick={() => alIr(s.id)}>
                    <span className="ico">{s.ico}</span>{s.et}
                    {cuentas[s.id] !== undefined && <span className="cont">{cuentas[s.id]}</span>}
                  </button>
                  {/* Las hijas de la sección encendida, indentadas debajo (R-01). */}
                  {!hablando && seccion === s.id && (hijas[s.id] ?? []).map((h) => (
                    <button key={h.id} className={"it hija" + (actual === h.id ? " on" : "")} onClick={() => alIr(h.id)}>
                      <span className="ico">{h.ico}</span>{h.et}
                      {cuentas[h.id] !== undefined && <span className="cont">{cuentas[h.id]}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
        <div className="grupo">{flujo ? "Tu coach" : "Agentes editoriales"}</div>
        <div className="items">
          {agentes.map(fila)}
          {/* El chat de reescritura no es un agente: es el hilo del proyecto donde habla el editor
              encarga al fantasma, al coach o al corrector (Agentes II, 17-sep). Pieza: Reescritura.tsx. */}
          {!flujo && (!q || "chat de reescritura".includes(q)) && (
            <button className={"conv" + (hablando === "reescritura" ? " on" : "")} onClick={() => alHablar("reescritura")}
              title="El hilo del proyecto: el editor habla contigo y convoca al escritor fantasma y al coach">
              {/* 17-sep (Rais): el chat de reescritura es de los tres que reescriben, y tiene que verse. */}
              <span className="caras-juntas">
                {["editor", "ghostwriter", "coach"].map((id) => <Cara key={id} id={id} nombre={id} />)}
              </span>
              <span className="qui">
                <span className="l1"><b>Chat de reescritura</b></span>
                <span className="l2 oficio">el editor, con el escritor fantasma y el coach</span>
              </span>
            </button>
          )}
        </div>
        {!equipo.length && <p className="vacio" style={{ padding: "8px" }}>{flujo ? "Cargando…" : "Cargando tu equipo…"}</p>}
        {q.length >= 3 && (
          <>
            <div className="grupo">En tus conversaciones</div>
            {!dir ? <p className="vacio" style={{ padding: "8px" }}>Abre un proyecto para buscar en sus conversaciones.</p>
              : hilos === null ? <p className="vacio" style={{ padding: "8px" }}>Buscando…</p>
              : !hallazgos.length ? <p className="vacio" style={{ padding: "8px" }}>Nada con «{busca}».</p>
              : hallazgos.slice(0, 30).map((h, i) => (
                <button key={h.agente.id + i} className="conv" onClick={() => { alHablar(h.agente.id); setBusca(""); }}>
                  {h.agente.id === "reescritura"
                    ? <span className="caras-juntas">{["editor", "ghostwriter", "coach"].map((id) => <Cara key={id} id={id} nombre={id} />)}</span>
                    : <Cara id={h.agente.id} nombre={h.agente.nombre} />}
                  <span className="qui">
                    <span className="l1"><b>{h.agente.nombre}</b></span>
                    <span className="l2">{h.mio ? "Tú: " : ""}{h.antes}<mark>{h.encontrado}</mark>{h.despues}</span>
                  </span>
                </button>
              ))}
          </>
        )}
        {equipo.length > 0 && !agentes.length && q && q.length < 3 && (
          <p className="vacio" style={{ padding: "8px" }}>Ningún agente encaja con «{busca}».</p>
        )}
      </div>

      <div className="cuenta">
        <button className="fila" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
          <span className="av">{iniciales}</span>
          {/* El nombre venía a fuego: cualquier cliente veía «Rais Busom» en SU panel. Sale del
              email de la licencia, que vive en su disco y no viaja a ninguna parte. */}
          <span className="quien"><b>{quien}</b><span>Editor Studio · v{version}</span></span>
          <span className="mas">⌄</span>
        </button>
        {menu && (
          <div className="menu-cuenta abierto">
            <button className="it" onClick={() => ir("ajustes")}><span className="ico">⚙</span>Ajustes</button>
            <button className="it" onClick={() => ir("suscripcion")}><span className="ico">◫</span>Suscripción</button>
            {/* Solo si hay contraseña. Sin candado puesto, «cerrar el panel» no cierra nada:
                era un botón que no hacía nada o, peor, dejaba al autor delante de una puerta
                que pedía una contraseña que no existía. */}
            {cerrado && <button className="it" onClick={alSalir}><span className="ico">⎋</span>Cerrar el panel</button>}
            <button className="it" onClick={alternarTema}><span className="ico">◐</span>Tema claro / oscuro</button>
            {/* Salir de la cuenta cierra también el motor: si no, el testigo seguiría abriendo el
                manuscrito a quien se siente después en este navegador. */}
            <button className="it" onClick={() => { setMenu(false); salir().finally(() => clerk.signOut()); }}><span className="ico">⏻</span>Cerrar sesión</button>
            <button className="it" onClick={() => ir("ayuda")}><span className="ico">?</span>Ayuda y soporte</button>
            <a className="it" href="mailto:soporte@humanink.io"><span className="ico">✉</span>Escribir a soporte</a>
          </div>
        )}
        <div className="gasto">
          <span>Gasto acumulado</span>
          <b>{gasto.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</b>
        </div>
      </div>
    </aside>
  );
}
